import { create } from 'zustand';
import type { ModalId, ModalState } from '../types/modal.types';

interface ModalStore {
  // State
  modals: ModalState[];

  // Actions
  openModal: (id: ModalId, props?: Record<string, unknown>) => void;
  closeModal: (id: ModalId) => void;
  closeAllModals: () => void;
  isModalOpen: (id: ModalId) => boolean;
  getModalProps: (id: ModalId) => Record<string, unknown> | undefined;
}

/**
 * Global modal state management with Zustand
 * Allows opening/closing modals from anywhere in the app
 */
export const useModalStore = create<ModalStore>((set, get) => ({
  modals: [],

  openModal: (id, props) => {
    set((state) => {
      // Check if modal is already open
      const existingModal = state.modals.find((m) => m.id === id);

      if (existingModal) {
        // Update existing modal props
        return {
          modals: state.modals.map((m) =>
            m.id === id ? { ...m, isOpen: true, props } : m
          ),
        };
      }

      // Add new modal
      return {
        modals: [...state.modals, { id, isOpen: true, props }],
      };
    });
  },

  closeModal: (id) => {
    set((state) => ({
      modals: state.modals.map((m) => (m.id === id ? { ...m, isOpen: false } : m)),
    }));

    // Remove from state after animation completes
    setTimeout(() => {
      set((state) => ({
        modals: state.modals.filter((m) => m.id !== id),
      }));
    }, 300); // Animation duration
  },

  closeAllModals: () => {
    set((state) => ({
      modals: state.modals.map((m) => ({ ...m, isOpen: false })),
    }));

    // Remove all from state after animation completes
    setTimeout(() => {
      set({ modals: [] });
    }, 300);
  },

  isModalOpen: (id) => {
    const modal = get().modals.find((m) => m.id === id);
    return modal?.isOpen ?? false;
  },

  getModalProps: (id) => {
    const modal = get().modals.find((m) => m.id === id);
    return modal?.props;
  },
}));
